// TODO: Fix no-non-null assertions
import { cloneDeep, drop, first, sortBy, times } from 'lodash'
import { isEmptyTextWrap } from '../utils/isEmptyTextWrap'
import { Node as ProseNode, Fragment, Slice, ResolvedPos } from 'prosemirror-model'
import { findParentNodeOfType } from 'prosemirror-utils'
import { Command } from 'common/Prose/Commands'
import { selectionIncludesNodeType } from '../Toolbar/components/Format/components/utils/selectionIncludesNodeType'
import { NodeSelection, TextSelection, Transaction } from 'prosemirror-state'
import { isEmptyNode } from '../utils/isEmptyNode'
import { Direction } from 'utils/coordUtils'
import { schema, nodes, ExtractStrongNodeSpecAttributes, StrongNodeType } from 'prose-core'
import { splitNodeArrayIntoChunks } from '../utils/splitNodeArrayIntoChunks'
import {
  isDroppingListItemBelowList,
  isDroppingListItemInsideList,
  isListItemChangeDepthOperation,
} from './columns/list_item'
import { isDescendantOrSame } from 'proseShared/utils/isDescendantOrSame'
import { isListItem } from '../utils/isListItem'

type columnData = [start: number, end: number]
type columnGroupData = [columnData, columnData] | [columnData, columnData, columnData]

enum columnContainsColumnResult {
  aContainsB,
  bContainsA,
  neitherContainsOther,
}

/**
 * Given two position tuples in the form `[start0, end0]` and `[start1, end1]`, check whether one fully encloses the
 * other
 * @param colA `columnData` for the first column
 * @param colB `columnData` for the second column
 * @returns
 */
function columnContainsColumn(colA: columnData, colB: columnData): columnContainsColumnResult {
  const [start0, end0] = colA
  const [start1, end1] = colB
  if (start1 >= start0 && start1 <= end0 && end1 >= start0 && end1 <= end0) {
    return columnContainsColumnResult.aContainsB
  }
  if (start0 >= start1 && start0 <= end1 && end0 >= start1 && end0 <= end1) {
    return columnContainsColumnResult.bContainsA
  }
  return columnContainsColumnResult.neitherContainsOther
}

/**
 * When transitioning into a two-column column_group, we need to check whether the content of the newly added column
 * was part of the content that will form the first column (e.g. when dragging an image out of a list and into its own
 * column). If so, we need to assemble a different transaction:
 * 1. remove the dragged content from the source node
 * 2. make a column with the remaining content
 * 3. make a column with the content we deleted in step 1
 * if we didn't need to do this, we return false, signaling that the code should handle the operation the normal way
 * @param columnGroupData [[start0, end0], [start1, end1]]
 * @param tr state.tr
 * @returns `true` if it needed to handle nested positions, `false` if it didn't do anything
 */
const maybeHandleNestedWrapping = (columnGroupData: [columnData, columnData], tr: Transaction) => {
  const containResult = columnContainsColumn(...columnGroupData)
  if (containResult !== columnContainsColumnResult.neitherContainsOther) {
    let [start0] = columnGroupData[containResult === columnContainsColumnResult.aContainsB ? 0 : 1]
    let [start1, end1] = columnGroupData[containResult === columnContainsColumnResult.aContainsB ? 1 : 0]

    const removedNodes: ProseNode[] = []
    tr.doc.nodesBetween(start1, end1, (node, pos) => {
      if (pos < start1 || pos > end1) return
      if (removedNodes.some((n) => isDescendantOrSame(n, node))) return
      if (isListItem(node.type)) {
        const parent = tr.doc.resolve(pos)?.node()
        if (parent && parent.type.groups.includes('list')) {
          removedNodes.push(parent.type.create(cloneDeep(parent.attrs), [node]))
        }
        return
      }
      removedNodes.push(node)
    })
    tr.delete(start1, end1)
    const keptNode = tr.doc.resolve(start0).nodeAfter
    if (!keptNode) return false
    const columns = [schema.nodes.column.create({}, [keptNode]), schema.nodes.column.create({}, removedNodes)]
    if (containResult === columnContainsColumnResult.bContainsA) {
      columns.reverse()
    }
    const colGroupNode = schema.nodes.column_group.create({}, columns)
    tr.replace(start0, start0 + keptNode.nodeSize, new Slice(Fragment.from(colGroupNode), 0, 0))
    return true
  }
  return false
}

/**
 * Applies (but doesn't dispatch) a transaction that wraps **all the nodes** between `start` and `end` in a column group
 * consisting of `numColumns` many columns. Stuffs all the nodes in the leftmost column and leaves the other ones empty
 * @param columnGroupData [[start0, end0], [start1, end1]] - the positions of the columns
 * @param tr state.tr
 * @param direction the direction of the dragMove (if applicable), aka wether the column is being added to the left or to the right
 * @param turn_into_distribute whether to use the "distribute content into columns" experimental feature
 * @returns a new transaction. You probably don't need this since it mutates `tr`
 */
export const _wrapInColumnGroup = (
  columnGroupData: columnGroupData,
  tr: Transaction,
  direction?: Direction,
  turn_into_distribute?: boolean,
): void => {
  /**
   * We are wrapping things into (more than 1) columns - there is no such thing as wrapping in a single column
   * 1. Create a column group
   * 2. Fill it with N columns, where N is the number of newly added columns
   *   2.1. Fill the first column with the content of the selection
   *   2.2. Leave the other columns empty
   * 3. Replace the selection with the created column group
   */
  const desiredNumColumns = columnGroupData.length
  let positionsToHandle: Array<[start: number, end: number]> = []

  if (columnGroupData.length === 2 && maybeHandleNestedWrapping(columnGroupData, tr)) {
    return
  }

  let columnNodes: ProseNode[] = []

  if (turn_into_distribute) {
    // experimental `turn_into_distribute` flag: distribute the content of the selection into columns
    const firstColumnData = columnGroupData[0]
    const [minPos, maxPos] = firstColumnData
    let [innerMinPos, innerMaxPos, foundNodes] = expandRangeToTopLevelNode(minPos, maxPos, tr.doc)
    positionsToHandle.push([innerMinPos, innerMaxPos])

    const nodeChunks = splitNodeArrayIntoChunks(foundNodes, desiredNumColumns)

    columnNodes = times(desiredNumColumns, (colIdx) => {
      if (columnGroupData[colIdx].length > 1 || nodeChunks[colIdx].length > 0) {
        // make a column with the content of the selection
        return schema.nodes.column.create({}, nodeChunks[colIdx])
      } else {
        // make an empty column
        return schema.nodes.column.create({}, schema.nodes.paragraph.create({}))
      }
    })
  } else {
    // normal behavior: put the content of the selection into the first column, and leave the other columns empty
    columnNodes = times(desiredNumColumns, (colIdx) => {
      if (columnGroupData[colIdx].length > 1) {
        if (columnGroupData[colIdx][0] == null || columnGroupData[colIdx][1] == null) {
          throw new Error(`Encountered invalid column data for column ${colIdx}: ${columnGroupData[colIdx]}`)
        }
        const isFirstItemOfColumnListItem = isListItem(tr.doc.resolve(columnGroupData[colIdx][0]).nodeAfter?.type)

        if (isFirstItemOfColumnListItem) {
          const $draggedPos = tr.doc.resolve(columnGroupData[colIdx][0])
          const listNode = $draggedPos.node()
          if (!$draggedPos.nodeAfter) {
            throw new Error('dragged node position is invalid')
          }
          const wrappedListItem = listNode.type.create(listNode.attrs, $draggedPos.nodeAfter, listNode.marks)
          const positionToHandle = columnGroupData[colIdx]
          if ($draggedPos.nodeAfter === listNode.firstChild) {
            // we're going to delete the first item of the list, so we need to expand the left part
            // of the range to include the list itself (otherwise we'd leave an empty list item
            // behind)
            positionToHandle[0] -= 1
          }
          if ($draggedPos.nodeAfter === listNode.lastChild) {
            // we're going to delete the last item of the list, so we need to expand the right part
            // of the range to include the list itself (otherwise we'd leave an empty list item
            // behind)
            positionToHandle[1] += 1
          }
          positionsToHandle.push(positionToHandle)
          return schema.nodes.column.create({}, wrappedListItem)
        } else {
          let [innerMinPos, innerMaxPos, foundNodes] = expandRangeToTopLevelNode(
            columnGroupData[colIdx][0],
            columnGroupData[colIdx][1],
            isFirstItemOfColumnListItem ? tr.doc.resolve(columnGroupData[colIdx][0]).node() : tr.doc,
          )
          positionsToHandle.push([innerMinPos, innerMaxPos])
          return schema.nodes.column.create({}, foundNodes)
        }
      } else {
        return schema.nodes.column.create({}, schema.nodes.paragraph.create({}))
      }
    })
  }

  /**
   * The following code accounts for the case where you are dragging "upwards" or "downwards" and dropping in such a
   * way as to create a stack, while skipping over some content.
   * We always want to replace the destination with the side-stack, and delete the dragged node, but we need to figure
   * out what those are based on `positionsToHandle`, while remembering that this function can be called by a non-drag
   * operation
   */
  const sortedPositionsToHandle = sortBy(positionsToHandle, [0, 1])
  if (
    (direction === Direction.left && positionsToHandle[0][0] < positionsToHandle[1][0]) ||
    (direction === Direction.right && positionsToHandle[0][0] > positionsToHandle[1][0])
  ) {
    sortedPositionsToHandle.reverse()
  }

  if (sortedPositionsToHandle.length === 0) {
    throw new Error('sortedPositionsToHandle is empty')
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const [replaceStartPos, replaceEndPos] = first(sortedPositionsToHandle)! // we know this is non-null because of the check above
  const otherPositions = drop(sortedPositionsToHandle, 1)

  const colGroupNode = schema.nodes.column_group.create({}, columnNodes)

  if (tr.doc.resolve(replaceStartPos)?.parent?.type !== schema.nodes.column) {
    tr.replace(replaceStartPos, replaceEndPos, new Slice(Fragment.from(colGroupNode), 0, 0))
    otherPositions.forEach(([start, end]) => {
      const mappedStart = tr.mapping.map(start)
      const parent = tr.doc.resolve(mappedStart).node()
      if (parent.type.groups.includes('list') && parent.childCount === 1) {
        // if we're replacing the only child of a list, we need to delete the list itself
        tr.delete(mappedStart - 1, tr.mapping.map(end))
        return
      }
      tr.delete(mappedStart, tr.mapping.map(end))
    })
  } else {
    // Our dragged node is inside a column group. Normally this would result in a column group being put inside the
    // existing column, and the drop node to be deleted, which is a bug. Instead, we flip the operation:
    // we replace the drop target with the column group we just created
    // and we delete the dragged node.
    // We don't bother cleaning up the column if it turns out to be empty, instead relying on the parent function to
    // call `_maybeDeleteLeftoverColumn` for us
    tr.replace(otherPositions[0][0], otherPositions[0][1], new Slice(Fragment.from(colGroupNode), 0, 0))
    tr.delete(tr.mapping.map(replaceStartPos), tr.mapping.map(replaceEndPos))
  }
}
/**
 * Adds a column to an existing column group. The column is inserted at a specific index,
 * @param columnData an array of [start,end] positions, which represent the stuff to insert into the new column
 * @param columnToInsert The column index to insert. Note you can only insert columns when the column_group has two
 * columns - never three, since that would exceed the maximum and create a four-column column_group, which is not possible
 * @param $colGroupPos resolved pos of the column group
 * @param tr state.tr
 */
export const _addColumnToColumnGroup = (
  columnData: columnData | [],
  columnToInsert: 0 | 1 | 2,
  $colGroupPos: ResolvedPos,
  tr: Transaction,
): void => {
  if (!$colGroupPos.nodeAfter) {
    throw new Error('column group position is invalid')
  }
  const posData = getColPosData({ pos: $colGroupPos.pos, node: $colGroupPos.nodeAfter })
  let insertionPos: number

  if (columnToInsert === 0) {
    insertionPos = posData[0][0]
  } else {
    insertionPos = posData[columnToInsert - 1][1]
  }

  if (columnData.length === 0) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    tr.insert(insertionPos, schema.nodes.column.createAndFill(null)!) // guaranteed to be non-null since we're inserting an empty column
  } else {
    const foundNodes = getTopmostNodesInRange(columnData, tr.doc).map((node, idx) => {
      if (isListItem(node.type)) {
        const parentList = tr.doc.resolve(columnData[idx]).node()
        return parentList.type.create(parentList.attrs, node, parentList.marks)
      }
      return node
    })
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    tr.insert(insertionPos, schema.nodes.column.createAndFill(null, foundNodes)!) // guaranteed to be non-null since we're inserting a non-empty column
  }
}
/**
 * Given a range, find out the top-most nodes whose positions are fully encompassed by the range
 * @example
 * ```
 * // if you have a doc like
 * //   1 99 doc
 * //   2 30 unordered_list
 * //   3 20 list_item
 * //   4 10 paragraph
 * getTopmostNodesInRange([3,10], doc) // returns [paragraph]
 * ```
 * @param range a tuple of positions in the form [start,end]
 * @param doc tr.doc or state.doc
 * @returns the topmost/shallowest nodes that are fully encompassed by `range`
 */
export const getTopmostNodesInRange = (range: columnData, doc: ProseNode): ProseNode[] => {
  const [start, end] = range
  const foundNodes: ProseNode[] = []
  doc.nodesBetween(start, end, (thisNode, posBeforeThisNode) => {
    if (posBeforeThisNode >= start && posBeforeThisNode + thisNode.nodeSize <= end) {
      foundNodes.push(thisNode)
      return false
    }

    return true // descend
  })
  return foundNodes
}

/**
 * Given a dragged column, a direction and a target column, return what the new column order should be after dropping
 * @param draggedIdx the index of the column that is being dragged
 * @param dropIdx the index of the column we are dropping onto (before or after it)
 * @param direction either `left` or `right`
 * @returns
 */
export function findNewOrder(draggedIdx: columnIdx, dropIdx: columnIdx, direction: Direction.left | Direction.right) {
  if (draggedIdx === dropIdx) return [0, 1, 2] as possibleColumnOrders

  return [0, 1, 2].flatMap((current) => {
    if (current === draggedIdx) {
      // first remove the element that will be moved
      return []
    }
    if (current === dropIdx) {
      // we found the element where it's moving to
      if (direction === Direction.right) {
        // put the moved element to the right/left of target, depending on direction
        return [dropIdx, draggedIdx]
      } else {
        return [draggedIdx, dropIdx]
      }
    }
    return [current] // this is the element that won't be affected
  }) as possibleColumnOrders
}

/**
 * If you are reordering columns (this is a 3-column group and you are dragging the only element of a column, and the
 * drop position is valid) then reorder them, otherwise return and let processing continue
 * @param direction either `left` or `right`
 * @returns `true` if it had to do the swapping
 */
export const maybeSwapColumns = (
  direction: Direction.left | Direction.right,
  $draggedPos: ResolvedPos,
  $dropTargetPos: ResolvedPos,
  tr: Transaction,
) => {
  if (isColumnSwapOperation($draggedPos, $dropTargetPos)) {
    const draggedIdx = $dropTargetPos.node().content.content.findIndex((col) => col === $draggedPos.node()) as columnIdx
    let dropIdx = $dropTargetPos
      .node()
      .content.content.findIndex((col) => col === $dropTargetPos.nodeAfter) as columnIdx

    if (draggedIdx === dropIdx) {
      return true
    }

    const $colGroupPos = tr.doc.resolve($dropTargetPos.before())

    _reorderColumns($colGroupPos, findNewOrder(draggedIdx, dropIdx, direction), tr)

    // since we're swapping, we don't care about removing leftover columns - there won't be any
    return true
  }
  return false // not a column swap
}

export const _applyDragMove = (
  direction: Direction,
  $draggedPos: ResolvedPos,
  $dropTargetPos: ResolvedPos,
  tr: Transaction,
  depth?: number,
) => {
  // get affected column
  let columnPosOfDraggedNode: number | undefined = undefined
  if ($draggedPos.parent.type === schema.nodes.column) {
    columnPosOfDraggedNode = $draggedPos.before()
  }
  switch (direction) {
    case Direction.top: {
      let nodeToDrop = $draggedPos.nodeAfter
      if (!nodeToDrop) return
      if (depth !== undefined && isListItemChangeDepthOperation(direction, $draggedPos, $dropTargetPos, depth)) {
        tr.setNodeMarkup($draggedPos.pos, undefined, { ...nodeToDrop.attrs, depth })
        break
      }
      const draggedNodeParent = $draggedPos.node()
      if (isDroppingListItemInsideList(direction, $draggedPos, $dropTargetPos)) {
        const dropTargetNodeParent = $dropTargetPos.node()
        if (draggedNodeParent.type !== dropTargetNodeParent.type) {
          // if the list types are not the same, we need to break up the lists
          splitListsAndInsert($draggedPos, $dropTargetPos, depth, direction, tr)
          break
        }
        nodeToDrop = (() => {
          // if the list types are the same, we can just move the list item
          if (
            isListItem(nodeToDrop.type) &&
            (nodeToDrop.attrs as ExtractStrongNodeSpecAttributes<typeof nodes.list_item | typeof nodes.vote_list_item>)
              .depth !== depth
          ) {
            nodeToDrop = nodeToDrop.type.create(
              Object.assign({}, nodeToDrop.attrs, { depth }),
              nodeToDrop.content,
              nodeToDrop.marks,
            )
          }
          return nodeToDrop
        })()
      } else if (isDroppingListItemBelowList(direction, $draggedPos, $dropTargetPos)) {
        const numListItemsInSourceList = $draggedPos.node().childCount
        const posOfLastListItemInSourceList = $draggedPos.posAtIndex(numListItemsInSourceList - 1)
        $dropTargetPos = tr.doc.resolve(posOfLastListItemInSourceList)
        // if $draggedPos === $dropTargetPos, the item would delete itself, causing a bug
        // the correct behaviour is to do nothing
        if ($draggedPos !== $dropTargetPos) {
          _applyDragMove(Direction.bottom, $draggedPos, $dropTargetPos, tr, depth)
        }
        return
      } else if (isListItem($draggedPos.nodeAfter?.type)) {
        // we are dropping a list item below a non-list, so we need to wrap the list item in a list
        // of the same type as the source
        nodeToDrop = draggedNodeParent.type.create(cloneDeep(draggedNodeParent), nodeToDrop, draggedNodeParent.marks)
      }
      if (
        isListItem(nodeToDrop.type) &&
        (nodeToDrop.attrs as ExtractStrongNodeSpecAttributes<typeof nodes.list_item | typeof nodes.vote_list_item>)
          .depth !== depth
      ) {
        nodeToDrop = nodeToDrop.type.create(
          Object.assign({}, nodeToDrop.attrs, { depth }),
          nodeToDrop.content,
          nodeToDrop.marks,
        )
      }

      tr.insert($dropTargetPos.pos, nodeToDrop)
      if (!$draggedPos.nodeAfter) return
      if (isListItem(nodeToDrop.type)) {
        if (draggedNodeParent.childCount > 1) {
          // If the source list still has items in it, remove just the item we dropped
          tr.delete(tr.mapping.map($draggedPos.pos), tr.mapping.map($draggedPos.pos + $draggedPos.nodeAfter.nodeSize))
        } else {
          // If the source list would become empty, remove the whole list
          tr.delete(tr.mapping.map($draggedPos.before()), tr.mapping.map($draggedPos.after()))
        }
      } else {
        tr.delete(tr.mapping.map($draggedPos.pos), tr.mapping.map($draggedPos.pos + $draggedPos.nodeAfter.nodeSize))
      }
      break
    }
    case Direction.bottom: {
      let nodeToDrop = $draggedPos.nodeAfter
      if (!nodeToDrop) return
      if (depth !== undefined && isListItemChangeDepthOperation(direction, $draggedPos, $dropTargetPos, depth)) {
        tr.setNodeMarkup($draggedPos.pos, undefined, { ...nodeToDrop.attrs, depth })
        break
      }
      const draggedNodeParent = $draggedPos.node()
      if (isDroppingListItemInsideList(direction, $draggedPos, $dropTargetPos)) {
        const dropTargetNodeParent = $dropTargetPos.node()
        if (draggedNodeParent.type !== dropTargetNodeParent.type) {
          // if the list types are not the same, we need to break up the lists
          splitListsAndInsert($draggedPos, $dropTargetPos, depth, direction, tr)
          break
        }
        nodeToDrop = (() => {
          // if the list types are the same, we can just move the list item
          if (
            isListItem(nodeToDrop.type) &&
            (nodeToDrop.attrs as ExtractStrongNodeSpecAttributes<typeof nodes.list_item | typeof nodes.vote_list_item>)
              .depth !== depth
          ) {
            nodeToDrop = nodeToDrop.type.create(
              Object.assign({}, nodeToDrop.attrs, { depth }),
              nodeToDrop.content,
              nodeToDrop.marks,
            )
          }
          return nodeToDrop
        })()
      } else if (isDroppingListItemBelowList(direction, $draggedPos, $dropTargetPos)) {
        // if we are dropping a list item below a list, we need to decide to either
        // 1. insert the list item into the list (by changing the drop target to point to the last
        //    element of the list instead of the list itself)
        // 2. insert the list item after the list (after wrapping the list_item in a list of the
        //    same type as the source)
        const dropTargetListType = $dropTargetPos.nodeAfter?.type
        if (dropTargetListType === draggedNodeParent.type) {
          // the list types are the same, we can just move the list item (case 1)
          // make the target be the last element of the list
          $dropTargetPos = tr.doc.resolve(
            $dropTargetPos.end() - ($dropTargetPos.nodeAfter?.lastChild?.nodeSize ?? 0) - 1,
          )
        } else {
          // the list types are different, we need to wrap the list item in a list of the same type
          // as the source (case 2)
          nodeToDrop = draggedNodeParent.type.create(cloneDeep(draggedNodeParent), nodeToDrop, draggedNodeParent.marks)
        }
      } else if (isListItem($draggedPos.nodeAfter?.type)) {
        // we are dropping a list item below a non-list, so we need to wrap the list item in a list
        // of the same type as the source
        nodeToDrop = draggedNodeParent.type.create(cloneDeep(draggedNodeParent), nodeToDrop, draggedNodeParent.marks)
      }
      if (!$dropTargetPos.nodeAfter || !$draggedPos.nodeAfter) return

      tr.insert($dropTargetPos.pos + $dropTargetPos.nodeAfter.nodeSize, nodeToDrop)

      if (isListItem(nodeToDrop.type)) {
        if (draggedNodeParent.childCount > 1) {
          tr.delete(tr.mapping.map($draggedPos.pos), tr.mapping.map($draggedPos.pos + $draggedPos.nodeAfter.nodeSize))
        } else {
          tr.delete(tr.mapping.map($draggedPos.before()), tr.mapping.map($draggedPos.after()))
        }
      } else {
        tr.delete(tr.mapping.map($draggedPos.pos), tr.mapping.map($draggedPos.pos + $draggedPos.nodeAfter.nodeSize))
      }
      break
    }
    case Direction.right:
      if (maybeSwapColumns(direction, $draggedPos, $dropTargetPos, tr)) {
        return
      }
      if (!$dropTargetPos.nodeAfter || !$draggedPos.nodeAfter) return
      if ($dropTargetPos.nodeAfter?.type === schema.nodes.column_group) {
        _addColumnToColumnGroup(
          [$draggedPos.pos, $draggedPos.pos + $draggedPos.nodeAfter.nodeSize],
          2,
          $dropTargetPos,
          tr,
        )
        tr.delete(tr.mapping.map($draggedPos.pos), tr.mapping.map($draggedPos.pos + $draggedPos.nodeAfter.nodeSize))
      } else if ($dropTargetPos.nodeAfter.type === schema.nodes.column) {
        _addColumnToColumnGroup(
          [$draggedPos.pos, $draggedPos.pos + $draggedPos.nodeAfter.nodeSize],
          1,
          tr.doc.resolve($dropTargetPos.before()),
          tr,
        )
        tr.delete(tr.mapping.map($draggedPos.pos), tr.mapping.map($draggedPos.pos + $draggedPos.nodeAfter.nodeSize))
      } else {
        _wrapInColumnGroup(
          [
            [$dropTargetPos.pos, $dropTargetPos.pos + $dropTargetPos.nodeAfter.nodeSize],
            [$draggedPos.pos, $draggedPos.pos + $draggedPos.nodeAfter.nodeSize],
          ],
          tr,
          direction,
        )
      }
      break
    case Direction.left:
      if (maybeSwapColumns(direction, $draggedPos, $dropTargetPos, tr)) {
        return
      }
      if (!$dropTargetPos.nodeAfter || !$draggedPos.nodeAfter) return
      if ($dropTargetPos.nodeAfter.type === schema.nodes.column_group) {
        _addColumnToColumnGroup(
          [$draggedPos.pos, $draggedPos.pos + $draggedPos.nodeAfter.nodeSize],
          0,
          $dropTargetPos,
          tr,
        )
        tr.delete(tr.mapping.map($draggedPos.pos), tr.mapping.map($draggedPos.pos + $draggedPos.nodeAfter.nodeSize))
      } else {
        _wrapInColumnGroup(
          [
            [$draggedPos.pos, $draggedPos.pos + $draggedPos.nodeAfter.nodeSize],
            [$dropTargetPos.pos, $dropTargetPos.pos + $dropTargetPos.nodeAfter.nodeSize],
          ],
          tr,
          direction,
        )
      }
      break
  }

  if (columnPosOfDraggedNode !== undefined) {
    _maybeDeleteLeftoverColumn(columnPosOfDraggedNode, tr)
  }
}
/**
 * Remove a column from a column group, and if it's the last column in the group, remove the group as well
 * Beware that this will not delete the contents of the column, and will instead "eject" them
 * @param columnToRemove column index to remove
 * @param $colGroupPos a resolved position pointing to the start of the column group (not immediately before it)
 * @param tr transaction
 */
export const _removeColumnFromColumnGroup = (columnToRemove: number, $colGroupPos: ResolvedPos, tr: Transaction) => {
  if ($colGroupPos.node()?.type !== schema.nodes.column_group) {
    throw new Error(
      `Cannot remove column ${columnToRemove} because "${$colGroupPos.nodeAfter?.type.name}" is not a column_group`,
    )
  }
  const colPosData = getColPosData({ pos: $colGroupPos.before(), node: $colGroupPos.node() })
  if (columnToRemove > colPosData.length - 1) {
    throw new Error(`Cannot remove column ${columnToRemove} out of bounds (max: ${colPosData.length - 1})`)
  }

  const getContentsOfColumn = (start: number, end: number, colNode: ProseNode) => {
    start = tr.mapping.map(start)
    end = tr.mapping.map(end)
    const content = (colNode.content.content ?? []).filter((node) => !isEmptyTextWrap(node))
    return content
  }
  if (colPosData.length === 2) {
    // remove everything
    const discardedColsContent = colPosData.flatMap((colPosData) => getContentsOfColumn(...colPosData))

    tr.replaceWith($colGroupPos.before(), $colGroupPos.after(), discardedColsContent)
  } else {
    // remove just the column
    const discardedContent = getContentsOfColumn(...colPosData[columnToRemove])
    const colGroupEndPos = tr.mapping.map($colGroupPos.after())
    tr.delete(colPosData[columnToRemove][0], colPosData[columnToRemove][1])
    tr.insert(tr.mapping.map(colGroupEndPos), discardedContent)
  }
}

/**
 * Given a selection,
 *   if the nodes contained within it are not wrapped in columns, then wrap them
 *   if the nodes are already wrapped in columns, then change the number of columns or unwrap them
 *
 * - When increasing the number of columns, it will add blank columns to the end
 * - When decreasing the number of columns, it will remove the rightmost columns and place the nodes in the rightmost remaining column
 * - Attention: This is a factory that returns a command
 * @param newColumnCount set the number of columns to this value
 * @param turn_into_distribute whether to use the "distribute content into columns" experimental feature
 */
export const onSelectCol: (newColumnCount: number, turn_into_distribute?: boolean) => Command =
  (newColumnCount, turn_into_distribute) => (state, dispatch) => {
    const { tr, selection, schema } = state

    const $colGroupPos = findParentNodeOfType(schema.nodes.column_group)(selection)
    const columnCount = $colGroupPos ? $colGroupPos.node.content.childCount : 1

    if (newColumnCount === columnCount) return false

    if ($colGroupPos) {
      // we are inside a column group
      if (newColumnCount === 1) {
        /**
         * We are unwrapping the content from the columns - in other words, we're getting rid of columns
         * 1. Get contents of the columns and combine them
         * 2. Replace the column group with the combined content
         */
        const colGroupContent = $colGroupPos.node.content
        const oldFrom = selection.from,
          oldTo = selection.to
        tr.replaceWith(
          $colGroupPos.pos,
          $colGroupPos.pos + $colGroupPos.node.nodeSize - 1,
          times(colGroupContent.childCount, (childIdx) => {
            const colContent = colGroupContent.child(childIdx).content
            // check if empty
            return times(colContent.childCount, (colChild) => colContent.child(colChild)).filter(
              (colChild) => !isEmptyTextWrap(colChild),
            )
          }).flat(),
        )
        if (tr.selection instanceof TextSelection) {
          // keep the previous selection
          // hardcoded `-2` because we went from `column_group->column->selection` to `selection`
          tr.setSelection(TextSelection.create(tr.doc, oldFrom - 2, oldTo - 2))
        }
      } else if (newColumnCount > columnCount) {
        // since we only support 3 columns, this is always 2->3
        _addColumnToColumnGroup([], 2, tr.doc.resolve($colGroupPos.pos), tr)
      } else {
        const numDiscardedCols = columnCount - newColumnCount
        times(numDiscardedCols, (idx) => {
          const colIdxToRemove = columnCount - 1 - idx
          _removeColumnFromColumnGroup(colIdxToRemove, tr.doc.resolve($colGroupPos.pos + 1), tr)
        })
      }
    } else if (newColumnCount > 1) {
      // we are turning something into a column group
      if (selectionIncludesNodeType(schema.nodes.column_group, state)) return false // no column inception

      const emptyColumns = new Array(newColumnCount - 1).fill([])
      const oldFrom = selection.from,
        oldTo = selection.to
      _wrapInColumnGroup([[oldFrom, oldTo], ...emptyColumns] as any, tr, undefined, turn_into_distribute)
      if (tr.selection instanceof TextSelection) {
        if (turn_into_distribute) {
          // `oldFrom` just needs to be adjusted to lie within the column_group (+2 because column_group->column)
          // `oldTo` needs more adjustment because we don't know how many content transitions have taken place
          // since some columns can be empty (or paragraphs have been broken up). So we just use tr.mapping.map
          // and apply an offset
          tr.setSelection(TextSelection.create(tr.doc, oldFrom + 2, tr.mapping.map(oldTo) - 2))
        } else {
          // keep the previous selection
          // hardcoded `+2` because we went from `selection` to `column_group->column->selection`
          tr.setSelection(TextSelection.create(tr.doc, oldFrom + 2, oldTo + 2))
        }
      }
    }
    dispatch?.(tr)
    return true
  }

/**
 * Given a column_group CNP, return an array of "position data" (`[start, end, node]`) for each column inside it
 * @param $colGroupCNP A content node with position where the node is a column_group
 */
export const getColPosData = ($colGroupCNP: { pos: number; node: ProseNode }) => {
  const posList: Array<[start: number, end: number, node: ProseNode]> = []
  $colGroupCNP.node.nodesBetween(0, $colGroupCNP.node.nodeSize - 2, (node, colRelativePos) => {
    if (node.type === schema.nodes.column) {
      posList.push([$colGroupCNP.pos + colRelativePos + 1, $colGroupCNP.pos + colRelativePos + node.nodeSize + 1, node])
    }
    return false
  })
  return posList
}

/**
 * Given a range of positions (`start` and `end`), find all the nodes that touch that range, expanding it until you hit
 * the top-level node (`doc`).
 *
 * This is used for when you have a selection inside a deeply nested tree of nodes, e.g. an image inside a list inside a
 * table, and you click the button to make it into a column group.
 * The newly-created column group should include not only the image, but the list and the table too. This function helps
 * you find those nodes and the relevant range that contains them.
 *
 * @param start pos
 * @param end pos
 * @param doc tr.doc or state.doc
 * @returns the expanded range and all the nodes contained within said range.
 */
export const expandRangeToTopLevelNode = (
  start: number,
  end: number,
  doc: ProseNode,
): [start: number, end: number, foundNodes: ProseNode[]] => {
  let minPos = Infinity
  let maxPos = 0
  const foundNodes: ProseNode[] = []
  doc.nodesBetween(start, end, (node, pos) => {
    if (pos === 0 && node.type === schema.nodes.doc) {
      // don't include the doc node, but do descend if you find it.
      // also, zero is a valid minPos (meaning your selection encompasses the first node)
      // note: this code doesn't seem to run in any case but I swear I saw it hit this once, so I'm scared of deleting it
      minPos = 0
      return true
    }
    if ([schema.nodes.column, schema.nodes.column_group].includes(node.type as StrongNodeType<any, typeof schema>)) {
      return true
    }
    minPos = Math.min(minPos, pos)
    maxPos = Math.max(maxPos, pos + node.nodeSize)
    foundNodes.push(node.copy(Fragment.from(node.content)))
    return false
  })
  return [minPos, maxPos, foundNodes]
}

/**
 * This function will
 * 1. split the list at the given position
 * 2. wrap the node to drop (a list_item) in a list of the same type as the source list
 * 3. insert the new list (containing the node to drop) between the two split lists
 * 4. remove the node to drop from the source list
 */
function splitListsAndInsert(
  $draggedPos: ResolvedPos<any>,
  $dropTargetPos: ResolvedPos<any>,
  depth: number | undefined,
  direction: Direction.top | Direction.bottom,
  tr: Transaction<any>,
) {
  // we are guaranteed the following:
  // 1. What we are dragging is a list_item
  // 2. The drop target is a list_item
  // 3. The source list and the target list are DIFFERENT types
  let nodeToDrop = $draggedPos.nodeAfter
  if (!nodeToDrop) return
  const draggedNodeParent = $draggedPos.node()
  const dropTargetNodeParent = $dropTargetPos.node()
  const startOfListPos = $dropTargetPos.before() // resolve(startOfListPos).nodeAfter points to the list itself (not the list_item)
  if (!$dropTargetPos.nodeAfter) return
  const endOfDropTargetListItemPos = (() => {
    if (direction === Direction.top) {
      return $dropTargetPos.pos // resolve(endOfDropTargetListItem).nodeAfter points to the target list_item (the one we're dropping into)
    } else {
      return $dropTargetPos.pos + $dropTargetPos.nodeAfter.nodeSize // resolve(endOfDropTargetListItem).nodeAfter points to the next list item after the one we're dropping into
    }
  })()

  const insertedList = draggedNodeParent.type.create(
    draggedNodeParent.attrs,
    nodeToDrop.type.create(Object.assign({}, nodeToDrop.attrs, { depth }), nodeToDrop.content, nodeToDrop.marks),
    draggedNodeParent.marks,
  )

  let didSplit = (() => {
    /**
     * If the list we're dropping into
     * - has more than one item
     * - and we're not dropping at the start or end of the list
     * Then we need to split the list into two lists, and insert the new list between them
     * Otherwise, we just insert the new list before/after (depending on `direction`) the list we're dropping into
     */
    const listHasMoreThanOneItem = dropTargetNodeParent.childCount > 1
    const isDroppingAtTheEndOfTheList =
      direction === Direction.bottom && endOfDropTargetListItemPos === tr.doc.resolve(startOfListPos + 1)?.after() - 1
    const isDroppingAtTheStartOfTheList =
      direction === Direction.top && endOfDropTargetListItemPos === tr.doc.resolve(startOfListPos + 1).before() + 1
    if (listHasMoreThanOneItem && !isDroppingAtTheEndOfTheList && !isDroppingAtTheStartOfTheList) {
      tr.split(endOfDropTargetListItemPos, 1)
      return true
    }
  })()

  if (didSplit) {
    /**
     * Regardless of direction, we insert the new list AFTER the first list we created by splitting
     * (a.k.a. between the two split lists).
     * We could insert it BEFORE the second list instead, or pick one based on direction, but it
     * would have the same effect, so instead we just pick AFTER arbitrarily.
     */
    tr.insert(tr.doc.resolve(startOfListPos + 1).after(), insertedList)
  } else {
    /**
     * If we didn't split the list, we just insert the new list before/after
     * (depending on`direction`) the list we're dropping into
     */
    if (direction === Direction.top) {
      tr.insert(tr.doc.resolve(startOfListPos + 1).before(), insertedList)
    } else {
      tr.insert(tr.doc.resolve(startOfListPos + 1).after(), insertedList)
    }
  }

  if (draggedNodeParent.childCount > 1) {
    if (!$draggedPos.nodeAfter) return
    // If the source list still has items in it, remove just the item we dropped
    tr.delete(tr.mapping.map($draggedPos.pos), tr.mapping.map($draggedPos.pos + $draggedPos.nodeAfter.nodeSize))
  } else {
    // If the source list would become empty, remove the whole list
    tr.delete(tr.mapping.map($draggedPos.before()), tr.mapping.map($draggedPos.after()))
  }
}

/**
 * If deleting the last item of a column, remove that column.
 * If that causes the column_group to be left with only one column, unwrap the column group too
 * @param columnPosOfDraggedNode the position of the column that the dragged node is in
 * @param tr transaction
 */
function _maybeDeleteLeftoverColumn(columnPosOfDraggedNode: number, tr: Transaction<any>) {
  columnPosOfDraggedNode = tr.mapping.map(columnPosOfDraggedNode)
  const $column = tr.doc.resolve(columnPosOfDraggedNode)
  if ($column.nodeAfter?.type !== schema.nodes.column) return
  const $colGroupPos = tr.doc.resolve($column.before() + 1)
  if (isEmptyNode($column.nodeAfter)) {
    _removeColumnFromColumnGroup($column.index(), $colGroupPos, tr)
  }
}

/**
 * Given `$dragPos` and `$dropPos`, return whether dropping here would result in the column group being removed
 * (because it would result in a single column)
 * This occurs when you are dragging the last node of a column in a two-column column group, and dropping onto the other
 * column
 * @param $dragPos The resolved position of the node that is being dragged
 * @param $dropPos The resolved position of the node to drop onto
 * @returns true if the drop would result in the column group being undone (because it would result in a single column),
 * false otherwise
 */
export const wouldResultInSingleColumn = ($dragPos: ResolvedPos, $dropPos: ResolvedPos) => {
  return (
    $dragPos.node().type === schema.nodes.column && // dragging from a column
    $dragPos.node().childCount === 1 && // the node is the only (last) child of the column
    $dropPos.node().type === schema.nodes.column && // target is also a column
    $dropPos.node(-1) === $dragPos.node(-1) && // target belongs to the same column_group as the dragged node
    $dropPos.node(-1).childCount === 2 // column_group only has 2 columns
  )
}

export type columnIdx = 0 | 1 | 2
export type possibleColumnOrders = [0, 1, 2] | [0, 2, 1] | [1, 0, 2] | [1, 2, 0] | [2, 0, 1] | [2, 1, 0]

export const _reorderColumns = ($colGroupPos: ResolvedPos, newOrder: possibleColumnOrders, tr: Transaction<any>) => {
  const colGroupNode = $colGroupPos.nodeAfter
  if (!colGroupNode || colGroupNode.type !== schema.nodes.column_group) return tr

  const newContent = newOrder.map((newIdx) => colGroupNode.content.content[newIdx])
  tr.replaceWith($colGroupPos.pos + 1, $colGroupPos.pos + colGroupNode.nodeSize, newContent)
  return tr
}

export const isLastOfColumn = ($dragPos: ResolvedPos) => {
  const maybeColumnNode = $dragPos.node()
  return (
    maybeColumnNode.type === schema.nodes.column && // dragging from a column
    maybeColumnNode.childCount === 1 && // column only has 1 child
    maybeColumnNode.firstChild === $dragPos.nodeAfter // the dragged node is that only child
  )
}

export const isColumnSwapOperation = ($dragPos: ResolvedPos, $dropPos: ResolvedPos) => {
  if (!$dropPos.nodeAfter) return false
  return (
    $dropPos.nodeAfter.type === schema.nodes.column && // target is a column
    $dropPos.node(1).childCount === 3 && // we only swap when there are 3 columns, 2-column swapping is handled differently
    isLastOfColumn($dragPos) && // dragging the last item of the column group
    $dropPos.node(1) === $dragPos.node(1) // target is in the same column group as source
  )
}

/**
 * Given a transaction and a node, try to find the (new) position of the node in the new doc
 * @param tr Transaction
 * @param nodeToFind Prosemirror node to find
 * @returns The position of the node, or `null` if we couldn't find it. Beware that the found position could be 0,
 * so be careful with if statements.
 *
 * @example
 * ```typescript
 * const someNode = $someResolvedPos.nodeAfter
 * _doSomeOperation(tr)
 * // ... a bunch more stuff modifies the transaction ...
 * _doMoreStuff(tr)
 * const newNodePos = findNewPos(tr, someNode)
 * if(newNodePos !== null) { // careful with if statements! newNodePos can be zero!
 *   doSomethingWith(newNodePos)
 * }
 * ```
 */
export const findNodePositionInTransaction = (tr: Transaction, nodeToFind: ProseNode): number | null => {
  let foundPos: number | undefined = undefined
  tr.doc.nodesBetween(0, tr.doc.content.size, (node, pos) => {
    if (foundPos !== undefined) return false // return early (don't descend) because we already found the node
    if (node.eq(nodeToFind)) {
      foundPos = pos
    }
    return true // otherwise descend
  })

  return foundPos ?? null
}

/**
 * If you have a 2-column group, and you select the last item in it as a NodeSelection and then delete it,
 * this function deletes said node and ensures a malformed column_group is not left behind
 */
export const maybeDeleteColumnItem: Command = ({ selection, tr }, dispatch) => {
  if (!(selection instanceof NodeSelection)) return false
  const parentNode = selection.$from.node()
  if (
    parentNode.type === schema.nodes.column &&
    parentNode.childCount === 1 &&
    parentNode.firstChild?.eq(selection.node) &&
    /**
     * This next statement is a bit redundant.
     * It is not necessary to run this function when going 3->2 because the column will be deleted anyway
     * (handled automatically by prosemirror due to our schema forbidding empty columns)
     */
    selection.$from.node(-1).childCount === 2 //column_group only has 2 columns
  ) {
    const columnIndex = selection.$from.index(-1)
    const colGroupPos = selection.$from.before(-1) + 1
    // replace the item with an empty paragraph so that we can use `_removeColumnFromColumnGroup`
    // otherwise it would delete the column and then our index would be wrong
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    tr.replaceSelectionWith(schema.nodes.paragraph.create({})!) // guaranteed to create a paragraph
    const $colGroupPos = tr.doc.resolve(colGroupPos)
    _removeColumnFromColumnGroup(columnIndex, $colGroupPos, tr)
    dispatch?.(tr)
    return true
  }
  return false
}
